import React from 'react';
import {
  Table,
  useTableState,
  useFlashMessage,
  CustomActionWithPayload,
  ColumnDefinition,
  RegularTitle,
  RowDefinition,
  TagsFooterProps,
  palettes
} from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';
import { AgFinancing } from '@indigo-ag/schema';
import { Link } from 'react-router-dom';
import { faMinusCircle } from '@fortawesome/pro-solid-svg-icons';

import i18next from 'core/i18n';
import { text, currency, TABLE_ACTIONS } from 'shared';
import { removeAgFinanceFromCessionGroup } from 'features/brazil-grower/queries';

interface NegotiationsListProps {
  negotiations: AgFinancing[];
  canRemoveNegotiations?: boolean;
}

type NegotiationsColumnId = 'name' | 'collateralType' | 'cpfOrCnpj' | 'campaign' | 'contractValue';
type NegotiationsActions = CustomActionWithPayload<'REMOVE_NEGOTIATION', AgFinancing['id']>;
type NegotiationsContext = { canRemoveNegotiations: boolean };

const columnDefinition: ColumnDefinition<
  AgFinancing,
  NegotiationsColumnId,
  NegotiationsContext,
  NegotiationsActions
>[] = [
  {
    id: 'name',
    header: text.toTitleCase(i18next.t('name')),
    cell: ({ row }) => {
      return (
        <Link to={`/br-growers/${row.id}`} target="_blank">
          <RegularTitle style={{ fontWeight: 400, color: 'inherit' }}>
            {text.toTitleCase(row.grower?.name || '')}
          </RegularTitle>
        </Link>
      );
    }
  },
  {
    id: 'collateralType',
    header: text.toTitleCase(i18next.t('guarantyType')),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.collateralType ? i18next.t(row.collateralType) : '-'} />
  },
  {
    id: 'campaign',
    header: i18next.t('campaign'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.campaign?.name || '-'} />
  },
  {
    id: 'contractValue',
    header: i18next.t('contractValue'),
    cell: ({ row, SimpleCell }) => {
      const contractValue = row.orderInfo?.opportunities?.reduce((p, c) => (p += Number(c.contractValue) || 0), 0);
      return <SimpleCell label={contractValue ? currency.formatBrazilCurrency(contractValue) : '-'} />;
    }
  }
];

const rowDefinition: RowDefinition<AgFinancing, NegotiationsColumnId, NegotiationsContext, NegotiationsActions> = {
  rowId: ({ row }) => row.id,
  actions: ({ row, ActionButton, onTableAction, definitionContext }) =>
    definitionContext?.canRemoveNegotiations ? (
      <ActionButton destructive onClick={() => onTableAction({ type: 'REMOVE_NEGOTIATION', payload: row.id })}>
        {i18next.t('remove')}
      </ActionButton>
    ) : null,
  actionsLocation: 'footer',
  footer: ({ row, TagsFooter }) => {
    const tags: TagsFooterProps['tags'] = [];

    if (row?.phase4?.postedToFund === false) {
      tags.push({
        icon: faMinusCircle,
        iconColor: palettes.brand.red.base,
        id: 'name',
        label: text.firstLetterUppercase(i18next.t('sentToFundFailed'))
      });
    }

    return <TagsFooter tags={tags} />;
  }
};

export const NegotiationsList: React.FunctionComponent<NegotiationsListProps> = ({
  negotiations,
  canRemoveNegotiations = false
}) => {
  /**
   * Mutations
   */
  const [removeAgFinanceFromCessionGroupFn] = useMutation(removeAgFinanceFromCessionGroup);

  const { addFlashMessage } = useFlashMessage();

  const tableState = useTableState<AgFinancing, NegotiationsColumnId, NegotiationsActions>(negotiations, {
    onTableAction: action => {
      switch (action.type) {
        case TABLE_ACTIONS.REMOVE_NEGOTIATION:
          removeAgFinanceFromCessionGroupFn({
            variables: {
              agFinancingId: action.payload
            }
          })
            .then(() => {
              addFlashMessage({
                content: text.firstLetterUppercase(i18next.t('negotiationRemovedSuccessfully')),
                title: '',
                variant: 'success'
              });
            })
            .catch((e: ApolloError) => {
              addFlashMessage({
                content: e.graphQLErrors[0]?.message || i18next.t('error'),
                title: '',
                variant: 'error'
              });
            });
          return;
        default:
          return;
      }
    }
  });

  return (
    <div>
      <Table
        label="Negotiations"
        {...tableState}
        definitionContext={{
          canRemoveNegotiations
        }}
        columnDefinition={columnDefinition}
        rowDefinition={rowDefinition}
      />
    </div>
  );
};
